import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { avisar, textoDeError } from './Dialogo';
import { descargarExcel, nombreArchivo, Hoja } from '../lib/excel';

/**
 * El DETALLE de una cartera (0.42.0): quiénes son los expedientes que cuenta una tarjeta de Equipo — sobre todo los
 * que no atiende nadie (a nombre de alguien que ya no está en Halory, casi siempre del Excel del CRM, o de nadie).
 * Lista paginada (50 por página, sólo lo que se ve), filtro por etapa y por nombre, liga a la Ficha, y "Bajar a
 * Excel" con TODO lo filtrado (teléfono, correo, a nombre de quién, etapa, visitas, lo comprado, si está en Halory
 * y quién la atiende allá) para repartirlo. API: GET crm/team/cartera.
 */
interface Fila {
  patient_id: string; nombre: string | null; a_nombre_de: string | null; asesoria: string | null; etapa: string;
  telefono: string | null; telefono2: string | null; correo: string | null; origen: string | null; llego: string | null;
  primera_visita: string | null; ultima_visita: string | null; visitas: number; comprado: number; proxima_cita: string | null;
  en_halory: boolean; atiende_halory: string | null; etapa_halory: string | null;
}
interface Resp { total: number; encontrados?: number; filtrados: number; porEtapa: Record<string, number>; pagina: number; por: number; paginas: number; recortado: boolean; contactos: boolean; filas: Fila[] }

const ETAPAS: [string | null, string][] = [[null, 'Todas'], ['LEAD', 'Lead'], ['PV', 'Primera visita'], ['SUB', 'Recompra'], ['UGC', 'UGC']];
const NOMBRE_ETAPA: Record<string, string> = { LEAD: 'Lead', PV: 'Primera visita', SUB: 'Recompra', UGC: 'UGC' };
const money = (n: number) => '$' + Math.round(n).toLocaleString('es-MX');
const dia = (s: string | null) => (s ? new Date(s.length === 10 ? `${s}T12:00:00` : s).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const hoyTxt = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' });

const CSS = `
.cd-fondo { position: fixed; inset: 0; background: rgba(30, 24, 16, .45); z-index: 60; display: flex; align-items: center; justify-content: center; padding: 16px; }
.cd-panel { width: min(1080px, 100%); max-height: calc(100dvh - 32px); display: flex; flex-direction: column; overflow: hidden; }
.cd-cab { padding: 14px 16px 10px; display: flex; gap: 10px; align-items: flex-start; justify-content: space-between; border-bottom: 1px solid var(--border); }
.cd-cab h3 { margin: 0; font-size: 17px; font-weight: 600; color: var(--gold-dark); }
.cd-cab p { margin: 2px 0 0; font-size: 12.5px; color: var(--ink-soft); }
.cd-herr { padding: 10px 16px; display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.cd-chip { border: 1px solid var(--border); background: var(--card); border-radius: 999px; padding: 5px 12px; font-size: 12.5px; font-weight: 500; cursor: pointer; color: var(--ink); white-space: nowrap; box-shadow: none; }
.cd-chip[aria-pressed="true"] { background: var(--gold); border-color: var(--gold); color: #fff; }
.cd-herr input { width: auto; flex: 1 1 180px; min-width: 0; padding: 8px 12px; font-size: 13.5px; }
.cd-cuerpo { overflow: auto; flex: 1; min-height: 0; }
.cd-tabla { width: 100%; border-collapse: collapse; font-size: 13px; }
.cd-tabla th { position: sticky; top: 0; background: var(--bg-alt); text-align: left; font-weight: 600; font-size: 12px; color: var(--ink-soft); padding: 8px 10px; white-space: nowrap; z-index: 1; }
.cd-tabla td { padding: 8px 10px; border-top: 1px solid var(--border); vertical-align: top; }
.cd-tabla td small { display: block; color: var(--ink-soft); font-size: 11.5px; }
.cd-pie { padding: 10px 16px; display: flex; gap: 8px; align-items: center; justify-content: space-between; border-top: 1px solid var(--border); flex-wrap: wrap; font-size: 12.5px; }
@media (max-width: 600px) { .cd-fondo { padding: 0; } .cd-panel { max-height: 100dvh; height: 100dvh; border-radius: 0; } }
`;

/** Las columnas del Excel (una fila por expediente). */
export function hojaCartera(nombre: string, filas: Fila[], conANombreDe: boolean): Hoja {
  const cols: Hoja['columnas'] = [
    { titulo: 'Nombre', ancho: 30 }, { titulo: 'Teléfono', tipo: 'texto', ancho: 16 }, { titulo: 'Teléfono 2', tipo: 'texto', ancho: 14 },
    { titulo: 'Correo', ancho: 28 }, { titulo: 'Etapa', ancho: 14 },
  ];
  if (conANombreDe) cols.push({ titulo: 'A nombre de (HUB)', ancho: 18 });
  cols.push({ titulo: 'En Halory', ancho: 10 }, { titulo: 'Atiende en Halory', ancho: 18 }, { titulo: 'Etapa en Halory', ancho: 14 },
    { titulo: 'Llegó por', ancho: 16 }, { titulo: 'Llegó', tipo: 'fechahora' }, { titulo: 'Primera visita', tipo: 'fecha', ancho: 13 },
    { titulo: 'Última visita', tipo: 'fecha', ancho: 13 }, { titulo: 'Visitas', tipo: 'numero' }, { titulo: 'Comprado', tipo: 'dinero' },
    { titulo: 'Próxima cita', tipo: 'fechahora' });
  return {
    nombre, columnas: cols,
    filas: filas.map((f) => [
      f.nombre, f.telefono, f.telefono2, f.correo, NOMBRE_ETAPA[f.etapa] || f.etapa,
      ...(conANombreDe ? [f.a_nombre_de || 'Sin asignar'] : []),
      f.en_halory, f.atiende_halory, f.etapa_halory, f.origen, f.llego, f.primera_visita, f.ultima_visita, f.visitas, f.comprado, f.proxima_cita,
    ]),
  };
}

/** Baja a Excel toda la cartera de esas asesorías (con los filtros que se pasen). */
export async function bajarCartera(titulo: string, asesoras: string[], filtros: { etapa?: string | null; q?: string } = {}): Promise<void> {
  const qs = new URLSearchParams({ asesoras: asesoras.join(','), todo: '1' });
  if (filtros.etapa) qs.set('etapa', filtros.etapa);
  if (filtros.q) qs.set('q', filtros.q);
  const r = await api<Resp>(`crm/team/cartera?${qs}`);
  if (!r.filas.length) { await avisar({ titulo: 'No hay nada que bajar', mensaje: 'Con esos filtros no queda ningún expediente.' }); return; }
  const variasPersonas = asesoras.length > 1 || asesoras.includes('sin');
  descargarExcel(nombreArchivo('cartera', titulo, hoyTxt()), [hojaCartera(titulo, r.filas, variasPersonas)]);
  if (r.recortado) await avisar({ titulo: 'Se bajaron los primeros 5,000', mensaje: `Con esos filtros hay ${r.filtrados.toLocaleString('es-MX')} expedientes; el archivo trae los primeros 5,000. Filtra por etapa para bajar el resto.` });
  if (!r.contactos) await avisar({ titulo: 'Sin teléfonos', mensaje: 'No se pudieron descifrar los datos de contacto de alguna fila, así que el archivo salió sin teléfonos ni correos.' });
}

export function CarteraDetalle({ titulo, subtitulo, asesoras, onClose }: { titulo: string; subtitulo?: string; asesoras: string[]; onClose: () => void }) {
  const [etapa, setEtapa] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [qListo, setQListo] = useState('');
  const [pagina, setPagina] = useState(1);
  const [d, setD] = useState<Resp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bajando, setBajando] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const variasPersonas = asesoras.length > 1 || asesoras.includes('sin');

  // La búsqueda espera a que se deje de teclear, y sólo regresa a la página 1 si de verdad cambió (si no, un
  // "Siguiente" tocado en el primer instante se perdía: el temporizador del arranque lo regresaba a la 1).
  useEffect(() => {
    const t = setTimeout(() => { const v = q.trim(); if (v !== qListo) { setQListo(v); setPagina(1); } }, 350);
    return () => clearTimeout(t);
  }, [q, qListo]);
  useEffect(() => {
    setError(null);
    const qs = new URLSearchParams({ asesoras: asesoras.join(','), pagina: String(pagina), por: '50' });
    if (etapa) qs.set('etapa', etapa);
    if (qListo) qs.set('q', qListo);
    let vivo = true;
    api<Resp>(`crm/team/cartera?${qs}`).then((r) => { if (vivo) setD(r); }).catch((e) => { if (vivo) setError(textoDeError(e)); });
    return () => { vivo = false; };
  }, [asesoras.join(','), etapa, qListo, pagina]);
  useEffect(() => {
    panel.current?.focus();
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.querySelector('[data-dialogo]')) onClose(); };
    document.addEventListener('keydown', tecla);
    const antes = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', tecla); document.body.style.overflow = antes; };
  }, []);

  const bajar = async () => {
    setBajando(true);
    try { await bajarCartera(titulo, asesoras, { etapa, q: qListo }); }
    catch (e) { await avisar({ titulo: 'No se pudo bajar el Excel', mensaje: textoDeError(e), tipo: 'error' }); }
    finally { setBajando(false); }
  };

  return (
    <div className="cd-fondo" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-cartera-detalle>
      <style>{CSS}</style>
      <div className="card cd-panel" role="dialog" aria-modal="true" aria-labelledby="cd-titulo" tabIndex={-1} ref={panel}>
        <div className="cd-cab">
          <div style={{ minWidth: 0 }}>
            <h3 id="cd-titulo">{titulo}</h3>
            <p>{d ? `${d.total.toLocaleString('es-MX')} expediente${d.total === 1 ? '' : 's'}` : 'Cargando…'}{subtitulo ? ` · ${subtitulo}` : ''}</p>
          </div>
          <button className="btn-ghost" style={{ padding: '7px 16px', fontSize: 13 }} onClick={onClose} data-cerrar>Cerrar</button>
        </div>
        <div className="cd-herr">
          {ETAPAS.map(([k, t]) => (
            <button key={t} className="cd-chip" aria-pressed={etapa === k} data-etapa={k || 'todas'}
              onClick={() => { setEtapa(k); setPagina(1); }}>
              {t}{d ? ` · ${(k ? d.porEtapa[k] || 0 : d.encontrados ?? d.total).toLocaleString('es-MX')}` : ''}
            </button>
          ))}
          <input placeholder="Buscar por nombre" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar por nombre" />
          <button className="btn-primary" style={{ padding: '8px 16px', fontSize: 13 }} disabled={bajando || !d || !d.filtrados} onClick={bajar} data-bajar-excel>
            {bajando ? 'Preparando…' : `Bajar a Excel${d ? ` (${d.filtrados.toLocaleString('es-MX')})` : ''}`}
          </button>
        </div>
        {d && !d.contactos && <div style={{ padding: '0 16px 8px', fontSize: 12.5, color: 'var(--warning)' }}>No se pudieron descifrar los teléfonos de alguna fila: la lista sale sin datos de contacto.</div>}
        <div className="cd-cuerpo">
          {error && <div style={{ padding: 16, color: 'var(--warning)' }}>{error}</div>}
          {!d && !error && <div className="muted" style={{ padding: 30, textAlign: 'center' }}>Cargando…</div>}
          {d && d.filas.length === 0 && <div className="muted" style={{ padding: 30, textAlign: 'center' }}>{d.total ? 'Nadie con esos filtros.' : 'Esta cartera está vacía.'}</div>}
          {d && d.filas.length > 0 && (
            <table className="cd-tabla" data-cartera-tabla>
              <thead><tr>
                <th>Nombre</th><th>Teléfono</th><th>Etapa</th>{variasPersonas && <th>A nombre de</th>}
                <th>En Halory</th><th>Última visita</th><th style={{ textAlign: 'right' }}>Comprado</th><th>Llegó</th>
              </tr></thead>
              <tbody>
                {d.filas.map((f) => (
                  <tr key={f.patient_id} data-fila={f.patient_id}>
                    <td><Link to={`/paciente/${f.patient_id}`} onClick={onClose}>{f.nombre || 'Sin nombre'}</Link>{f.proxima_cita && <small>Próxima cita: {dia(f.proxima_cita)}</small>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{f.telefono || '—'}{f.correo && <small>{f.correo}</small>}</td>
                    <td>{NOMBRE_ETAPA[f.etapa] || f.etapa}</td>
                    {variasPersonas && <td>{f.a_nombre_de || <span className="muted">nadie</span>}</td>}
                    <td>{f.en_halory ? <>{f.atiende_halory || <span className="muted">sin asignar allá</span>}{f.etapa_halory && <small>{f.etapa_halory}</small>}</> : <span className="muted">no está en Halory</span>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{dia(f.ultima_visita)}{f.visitas > 1 && <small>{f.visitas} visitas</small>}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{f.comprado ? money(f.comprado) : '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{dia(f.llego)}{f.origen && <small>{f.origen}</small>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {d && d.paginas > 1 && (
          <div className="cd-pie" data-cartera-paginas>
            <span className="muted">{((d.pagina - 1) * d.por + 1).toLocaleString('es-MX')}–{Math.min(d.pagina * d.por, d.filtrados).toLocaleString('es-MX')} de {d.filtrados.toLocaleString('es-MX')}</span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button className="btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }} disabled={d.pagina <= 1} onClick={() => setPagina(d.pagina - 1)}>Anterior</button>
              <button className="btn-ghost" style={{ padding: '6px 14px', fontSize: 13 }} disabled={d.pagina >= d.paginas} onClick={() => setPagina(d.pagina + 1)}>Siguiente</button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
